import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';

import { takeUntil, filter, Subject } from 'rxjs';

import { TwcWizardComponent } from '@topcon/twc-angular';

import { TranslationService } from '@mirage-projects/cw-commons';

import { UploadWizardService } from '../../../services/upload-wizard-service/upload-wizard.service';
import { WorkspaceService } from './workspace.service';

/**
 * Workspace options
 * This component is used as an optional step in the import workflow to set the workspace options
 */
@Component({
    selector: 'cw-workspace-options',
    templateUrl: 'workspace.component.html',
    standalone: false,
})
export class WorkspaceComponent implements OnInit, OnDestroy {
    /** Text that will be shown as a definition of the wizard in all the steps */
    @Input() subtitle = '';

    @Output() nameChange = new EventEmitter<boolean>();

    private onDestroy$: Subject<void> = new Subject();
    private readonly wizardTabName = 'WIZARD.TAB_DESTINATION_GROUP';

    constructor(
        public workspaceService: WorkspaceService,
        private uploadWizardService: UploadWizardService,
        private translationService: TranslationService
    ) {}

    ngOnInit(): void {
        this.listenToStepChanged();

        this.workspaceService.workspaceForm.valueChanges
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(() => this.nameChange.emit(this.workspaceService.workspaceForm.valid));
    }

    ngOnDestroy(): void {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }

    /**
     * Checks if the user is moving to the next step, so the validat    ions and summary can be done
     */
    listenToStepChanged(): void {
        this.setSummaryOnNextStep();
        this.setDefaultValuesSubscription();
    }

    private setSummaryOnNextStep(): void {
        this.uploadWizardService.stepChanged
            .pipe(
                takeUntil(this.onDestroy$),
                filter((wizardComponent) => {
                    const step = this.translationService.translate(this.wizardTabName);
                    return this.uploadWizardService.comparePreviousStep(wizardComponent, step);
                })
            )
            .subscribe((wizardComponent) => this.summary(wizardComponent.selectedIndex));
    }

    private setDefaultValuesSubscription(): void {
        this.uploadWizardService.stepChanged
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((wizardComponent) => this.setDefaultValues(wizardComponent));
    }

    private setDefaultValues(item: TwcWizardComponent): void {
        if (item.wizardSteps?.selectedItem.label !== this.translationService.translate(this.wizardTabName)) {
            return;
        }

        const form = this.workspaceService.workspaceForm;
        if (!form.get('newDatasetName')?.value) {
            form.patchValue({ newDatasetName: this.uploadWizardService.files[0].name });
        }
    }

    private summary(summaryPosition: number): void {
        const form = this.workspaceService.workspaceForm;
        const fields = [];

        fields.push({
            key: 'SCANPROC.DESTINATION_SUMMARY_NAME',
            value: form.get('newDatasetName')?.value,
        });

        fields.push({
            key: 'SCANPROC.DESTINATION_SUMMARY_EXISTING',
            value: form.get('selectedGroup')?.value.name,
        });

        this.uploadWizardService.summary[summaryPosition] = {
            title: this.wizardTabName,
            notice: 'SCANPROC.DESTINATION_SUMMARY',
            fields,
        };
    }
}
